// Registered Scales section for Settings → Scales tab.
// Scales are stored in config_settings as scale_<nickname> = JSON { ip, port, nickname, station_id }.
// Supervisors register scales here and assign them to stations.
// Modeled after RegisteredPrintersSection.tsx.

import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { toast } from 'react-hot-toast';
import { Scale, Plus, Trash2, Wifi, WifiOff, RefreshCw } from 'lucide-react';
import productionService from '../../../services/productionService';

/** Auth headers for scale-gateway REST calls (JWT required since PR #288). */
const scaleAuthHeaders = (): Record<string, string> => {
  const token = localStorage.getItem('authToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
};
import stationManagementService from '../../../services/stationManagementService';
import { useLanguage } from '../../../i18n/LanguageContext';

interface ScaleEntry {
  setting_key: string;
  config: {
    ip: string;
    port: number;
    nickname: string;
    station_id: number;
  };
}

interface Station {
  station_id: number;
  station_name: string;
}

const RegisteredScalesSection: React.FC = () => {
  const { t } = useLanguage();
  const queryClient = useQueryClient();
  const [showAddForm, setShowAddForm] = useState(false);
  const [newNickname, setNewNickname] = useState('');
  const [newIp, setNewIp] = useState('');
  const [newPort, setNewPort] = useState('1701');
  const [newStationId, setNewStationId] = useState('');

  // Fetch all config settings and filter scale_* entries
  const { data: scales = [], isLoading } = useQuery<ScaleEntry[]>(
    'registered-scales',
    async () => {
      const all = await productionService.getConfigSettings();
      return all
        .filter(s => s.setting_key.startsWith('scale_'))
        .map(s => {
          try {
            return { setting_key: s.setting_key, config: JSON.parse(s.setting_value) };
          } catch {
            return null;
          }
        })
        .filter(Boolean) as ScaleEntry[];
    },
    { refetchOnMount: 'always' }
  );

  // Fetch stations for the dropdown
  const { data: stations = [] } = useQuery<Station[]>(
    'scale-stations',
    async () => {
      const stns = await stationManagementService.getStations();
      return stns.map(s => ({ station_id: s.station_id, station_name: s.station_name }));
    },
    { refetchOnMount: 'always' }
  );

  // Poll live connection states from the gateway
  const { data: liveStatuses = [] } = useQuery<{ key: string; state: string }[]>(
    'scale-live-status',
    async () => {
      const res = await fetch('/api/scale/scales', { headers: scaleAuthHeaders() });
      if (!res.ok) return [];
      const json = await res.json();
      return json.scales || [];
    },
    { refetchInterval: 5000 }
  );
  const stateByKey = new Map(liveStatuses.map(s => [s.key, s.state]));

  // Check which station IDs are already assigned to a scale
  const assignedStationIds = new Set(scales.map(s => s.config.station_id));

  // Add scale mutation — writes to scale-gateway REST endpoint
  const addMutation = useMutation(
    async ({ nickname, ip, port, station_id }: { nickname: string; ip: string; port: number; station_id: number }) => {
      const response = await fetch('/api/scale/scales', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...scaleAuthHeaders() },
        body: JSON.stringify({ nickname, ip, port, station_id }),
      });
      if (!response.ok) throw new Error('Failed to register scale');
      return response.json();
    },
    {
      onSuccess: () => {
        toast.success(t('admin.scaleAdded'));
        queryClient.invalidateQueries('registered-scales');
        setShowAddForm(false);
        setNewNickname('');
        setNewIp('');
        setNewPort('1701');
        setNewStationId('');
      },
      onError: () => { toast.error(t('admin.scaleTestFailed')); },
    }
  );

  // Delete scale mutation
  const deleteMutation = useMutation(
    async (key: string) => {
      const response = await fetch(`/api/scale/scales/${key}`, { method: 'DELETE', headers: scaleAuthHeaders() });
      if (!response.ok) throw new Error('Failed to remove scale');
    },
    {
      onSuccess: () => {
        toast.success(t('admin.scaleDeleted'));
        queryClient.invalidateQueries('registered-scales');
      },
      onError: () => { toast.error('Failed to remove scale'); },
    }
  );

  // Test connection mutation
  const [testingKey, setTestingKey] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<{ key: string; success: boolean; info?: string; error?: string } | null>(null);

  const testMutation = useMutation(
    async (key: string) => {
      setTestingKey(key);
      const response = await fetch(`/api/scale/scales/${key}/test`, { method: 'POST', headers: scaleAuthHeaders() });
      if (!response.ok) throw new Error('Test request failed');
      return response.json();
    },
    {
      onSuccess: (data, key) => {
        setTestingKey(null);
        setTestResult({ key, ...data });
        if (data.success) {
          toast.success(`${t('admin.scaleTestSuccess')}: ${data.info || ''}`);
        } else {
          toast.error(`${t('admin.scaleTestFailed')}: ${data.error || ''}`);
        }
      },
      onError: (_, key) => {
        setTestingKey(null);
        setTestResult({ key, success: false, error: 'Request failed' });
        toast.error(t('admin.scaleTestFailed'));
      },
    }
  );

  // Reconnect mutation
  const reconnectMutation = useMutation(
    async (key: string) => {
      const response = await fetch(`/api/scale/scales/${key}/reconnect`, { method: 'POST', headers: scaleAuthHeaders() });
      if (!response.ok) throw new Error('Reconnect failed');
      return response.json();
    },
    {
      onSuccess: () => { toast.success(t('admin.scaleReconnectStarted')); },
      onError: () => { toast.error(t('admin.scaleTestFailed')); },
    }
  );

  const handleAdd = () => {
    if (!newNickname.trim() || !newIp.trim() || !newPort.trim() || !newStationId) return;
    // Basic IP validation
    if (!/^\d{1,3}(\.\d{1,3}){3}$/.test(newIp.trim())) {
      toast.error('Invalid IP address format');
      return;
    }
    addMutation.mutate({
      nickname: newNickname.trim(),
      ip: newIp.trim(),
      port: Number(newPort),
      station_id: Number(newStationId),
    });
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Scale className="w-5 h-5 text-secondary-500" />
          <h3 className="text-lg font-medium text-secondary-900 dark:text-white">{t('admin.registeredScales')}</h3>
        </div>
        <button
          onClick={() => setShowAddForm(!showAddForm)}
          className="flex items-center gap-1 px-3 py-1.5 text-sm bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors"
        >
          <Plus className="w-4 h-4" />
          {t('admin.addScale')}
        </button>
      </div>
      <p className="text-xs text-secondary-500 dark:text-gray-400 mb-4">{t('admin.registeredScalesDesc')}</p>

      {/* Add Scale Form */}
      {showAddForm && (
        <div className="mb-4 p-4 bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-700 rounded-lg">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
            <div>
              <label className="block text-xs font-medium text-secondary-700 dark:text-gray-300 mb-1">{t('admin.scaleNickname')}</label>
              <input
                type="text"
                value={newNickname}
                onChange={(e) => setNewNickname(e.target.value)}
                placeholder={t('admin.scaleNicknamePlaceholder')}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm bg-white dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-secondary-700 dark:text-gray-300 mb-1">{t('admin.scaleIpAddress')}</label>
              <input
                type="text"
                value={newIp}
                onChange={(e) => setNewIp(e.target.value)}
                placeholder={t('admin.scaleIpPlaceholder')}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-mono bg-white dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-secondary-700 dark:text-gray-300 mb-1">{t('admin.scalePort')}</label>
              <input
                type="number"
                value={newPort}
                onChange={(e) => setNewPort(e.target.value)}
                placeholder={t('admin.scalePortPlaceholder')}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-mono bg-white dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-secondary-700 dark:text-gray-300 mb-1">{t('admin.scaleStation')}</label>
              <select
                value={newStationId}
                onChange={(e) => setNewStationId(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm bg-white dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <option value="">{t('admin.selectScale')}</option>
                {stations.map((s) => (
                  <option
                    key={s.station_id}
                    value={s.station_id}
                    disabled={assignedStationIds.has(s.station_id)}
                  >
                    {s.station_name}{assignedStationIds.has(s.station_id) ? ` (${t('admin.assignedElsewhere')})` : ''}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex items-end gap-2">
              <button
                onClick={handleAdd}
                disabled={addMutation.isLoading || !newNickname.trim() || !newIp.trim() || !newStationId}
                className="px-4 py-2 bg-indigo-600 text-white text-sm rounded-md hover:bg-indigo-700 disabled:opacity-50 transition-colors"
              >
                {addMutation.isLoading ? '...' : t('common.save')}
              </button>
              <button
                onClick={() => { setShowAddForm(false); setNewNickname(''); setNewIp(''); setNewPort('1701'); setNewStationId(''); }}
                className="px-4 py-2 bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 text-sm rounded-md hover:bg-gray-300 transition-colors"
              >
                {t('common.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Scale List */}
      {isLoading ? (
        <div className="text-sm text-gray-500">{t('common.loading')}</div>
      ) : scales.length === 0 ? (
        <div className="text-sm text-gray-500 italic py-4">{t('admin.noScalesRegistered')}</div>
      ) : (
        <div className="border rounded-lg divide-y dark:border-gray-700 dark:divide-gray-700">
          {scales.map((scale) => {
            const station = stations.find(s => s.station_id === scale.config.station_id);
            const isTesting = testingKey === scale.setting_key;
            const result = testResult?.key === scale.setting_key ? testResult : null;
            const liveState = stateByKey.get(scale.setting_key);
            const isConnected = liveState === 'connected';

            return (
              <div key={scale.setting_key} className="px-4 py-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    {/* Live connection status dot */}
                    <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${
                      isConnected ? 'bg-green-500' : liveState === 'connecting' ? 'bg-yellow-500 animate-pulse' : 'bg-red-500'
                    }`} title={liveState || 'unknown'} />
                    <Scale className="w-4 h-4 text-gray-400" />
                    <div>
                      <div className="text-sm font-medium text-secondary-900 dark:text-white">{scale.config.nickname}</div>
                      <div className="flex items-center gap-3 flex-wrap">
                        {station && (
                          <span className="text-xs text-blue-600 dark:text-blue-400">{station.station_name}</span>
                        )}
                        <span className="text-xs font-mono text-gray-500 dark:text-gray-400">
                          {scale.config.ip}:{scale.config.port}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {/* Test result indicator */}
                    {result && (
                      <span className={`flex items-center gap-1 text-xs ${result.success ? 'text-green-600' : 'text-red-500'}`}>
                        {result.success ? <Wifi className="w-3 h-3" /> : <WifiOff className="w-3 h-3" />}
                        {result.success ? result.info : result.error}
                      </span>
                    )}
                    {/* Reconnect button — shown when not connected */}
                    {!isConnected && (
                      <button
                        onClick={() => reconnectMutation.mutate(scale.setting_key)}
                        disabled={reconnectMutation.isLoading}
                        className="flex items-center gap-1 px-2 py-1 text-xs bg-yellow-600 text-white rounded hover:bg-yellow-700 disabled:opacity-50 transition-colors"
                      >
                        <RefreshCw className="w-3 h-3" />
                        {t('admin.scaleReconnect')}
                      </button>
                    )}
                    {/* Test Connection button */}
                    <button
                      onClick={() => testMutation.mutate(scale.setting_key)}
                      disabled={isTesting}
                      className="px-2 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50 transition-colors"
                    >
                      {isTesting ? t('admin.scaleTesting') : t('admin.scaleTestConnection')}
                    </button>
                    {/* Delete button */}
                    <button
                      onClick={() => {
                        if (window.confirm(t('admin.confirmDeleteScale'))) {
                          deleteMutation.mutate(scale.setting_key);
                        }
                      }}
                      disabled={deleteMutation.isLoading}
                      className="p-1.5 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default RegisteredScalesSection;
